// MUI
import { Button, OutlinedInput, Slider, Switch, TextField, Tooltip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import React, { ReactElement, useState } from 'react';

import { ISSAIPadderAd } from '~/src/dash/api/network';
// Misc.
import { EBool } from '~/src/dash/api/types/bool';
import ConfirmationDialogButton from '~/src/dash/components/confirmation-dialog-button';
import { BetaTooltipIcon } from '~/src/dash/components/icons/beta-icon';
// Layout Related
import { Col, Cols, Row, Rows } from '~/src/dash/components/layout';
import { spacing } from '~/src/dash/components/layout/common';
import { Text } from '~/src/dash/components/text';
import { ButtonV2 } from '~/src/dash/publicaUI';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  canParseAsBase10Integer,
  forceNumberInBounds,
  generateTooltipForBetaFeatures,
  isRangeOfValues,
  potentiallyValidNonPositiveInteger,
} from '~/src/dash/utils/network-utils';
import {
  AUDIO_NORMALIZATION_SLIDER_MARKINGS,
  BETA_TOOLTIP_HEADER,
  BETA_TOOLTIP_TEXT,
  DEFAULT_AUDIO_NORMALIZATION_LEVEL,
  DISABLE_BETA_FEATURES_MODAL_DESCRIPTION,
  ENABLE_BETA_FEATURES_MODAL_DESCRIPTION,
  EPadderType,
  LOUDEST_AUDIO_NORMALIZATION_LEVEL,
  LUFS_TOOLTIP_HEADER,
  LUFS_TOOLTIP_TEXT,
  QUIETEST_AUDIO_NORMALIZATION_LEVEL,
} from '~/src/dash/views/networks/network-settings-view-constants';

export interface INetworkBetaFeaturesProps {
  // Use Beta Chip? (Only for new or cloned networks)
  betaChipRequired: boolean;
  // Opt in to Beta Features?
  EnableTranscoderV3: EBool;
  toggleEnableTranscoderV3: () => void;
  // Live Video Context
  EnableLiveContext: EBool;
  handleEnableLiveContextChange: () => void;
  LiveContextLookbackMinutes: number;
  // Audio Normalization
  EnableAudioNormalization: EBool;
  toggleEnableAudioNormalization: () => void;
  AudioNormalizeTarget: number;
  setAudioNormalizationTarget: (val: number) => void;
  // Padders (Jingle + Bumper)
  EnablePadders: boolean;
  toggleEnablePadders: () => void;
  uploadSSAIPadderAdMediafile: (
    changeEvent: React.ChangeEvent<HTMLInputElement>,
    padderType: EPadderType,
  ) => void;
  PadderAds: ISSAIPadderAd[];
  setSSAIPadderAds: (padders: ISSAIPadderAd[]) => void;
  publisherID: string;
  isSuper: () => boolean;
  handleStreamFormInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const NetworkBetaFeatures = ({
  betaChipRequired,
  EnableTranscoderV3,
  toggleEnableTranscoderV3,
  EnableAudioNormalization,
  toggleEnableAudioNormalization,
  AudioNormalizeTarget,
  setAudioNormalizationTarget,
  EnableLiveContext,
  handleEnableLiveContextChange,
  LiveContextLookbackMinutes,
  EnablePadders,
  toggleEnablePadders,
  uploadSSAIPadderAdMediafile,
  PadderAds,
  setSSAIPadderAds,
  publisherID,
  isSuper,
  handleStreamFormInputChange,
}: INetworkBetaFeaturesProps): ReactElement => {
  const transcoderV3Enabled = EnableTranscoderV3 === EBool.EBool_TRUE;
  const audioNormalizationEnabled = EnableAudioNormalization === EBool.EBool_TRUE;
  const paddersEnabled = EnablePadders;
  const bumperAd = PadderAds?.find(
    (padder) => padder.PadderType === EPadderType.PADDER_TYPE_BUMPER,
  );
  const jingleAd = PadderAds?.find(
    (padder) => padder.PadderType === EPadderType.PADDER_TYPE_JINGLE,
  );

  const [target, setTarget] = useState(-AudioNormalizeTarget);
  const [targetText, setTargetText] = useState(target.toString());

  const handleSliderChange = (_: React.ChangeEvent, value: number) => {
    setTarget(value);
    setTargetText(value.toString());
  };

  const handleAndCommitTextFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const level = event.target.value;
    if (potentiallyValidNonPositiveInteger(level)) {
      setTargetText(level);
    } else {
      let target = DEFAULT_AUDIO_NORMALIZATION_LEVEL;
      if (canParseAsBase10Integer(level)) {
        target = forceNumberInBounds(
          QUIETEST_AUDIO_NORMALIZATION_LEVEL,
          LOUDEST_AUDIO_NORMALIZATION_LEVEL,
          parseInt(level, 10),
        );
      }
      setTarget(target);
      setTargetText(target.toString());
      setAudioNormalizationTarget(-target);
    }
  };

  return (
    <Rows noPadding>
      <Row noPadding paddingBottom='16px'>
        <Cols style={{ alignItems: 'center' }}>
          <Col>
            <Text title bold>
              Beta Features
            </Text>
          </Col>
          <Col marginLeft='4px'>
            <Tooltip
              title={generateTooltipForBetaFeatures(BETA_TOOLTIP_TEXT, BETA_TOOLTIP_HEADER)}
              placement='right-start'
            >
              <InfoIcon style={{ width: '1rem', color: `${publicaColors.gray}` }} />
            </Tooltip>
          </Col>
          <Col>
            <ConfirmationDialogButton
              title='Verify your changes'
              description={
                transcoderV3Enabled
                  ? DISABLE_BETA_FEATURES_MODAL_DESCRIPTION
                  : ENABLE_BETA_FEATURES_MODAL_DESCRIPTION
              }
              acceptButtonTitle='Accept'
              acceptButtonCallBack={toggleEnableTranscoderV3}
              refuseButtonTitle='Cancel'
            >
              <Switch checked={transcoderV3Enabled} disabled={true} />
            </ConfirmationDialogButton>
          </Col>
        </Cols>
        <Text muted extraSmall>
          Select to enable beta features
        </Text>
      </Row>

      {/* Live Contextual Toggle */}
      {/* only if EnableLiveContext is not null */}
      {EnableLiveContext && (
        <Row smallVerticalPadding extraSmallHorizontalPadding>
          <Cols style={{ alignContent: 'center' }}>
            <Col style={{ width: '336px' }}>
              <Text bold>Enable Live Contextualization</Text>
              <Text mini color={publicaColors.gray}>
                Only available for HLS. Allows contextual segment targeting for this network. Please
                contact your account manager if you would like more information on activating Live
                Contextualization.
              </Text>
            </Col>
            <Col flex>
              <Switch
                checked={EnableLiveContext === EBool.EBool_TRUE}
                onChange={handleEnableLiveContextChange}
                disabled={!isSuper()}
              />
            </Col>
          </Cols>
        </Row>
      )}
      {EnableLiveContext === EBool.EBool_TRUE && (
        <Row smallVerticalPadding extraSmallHorizontalPadding>
          <Cols style={{ alignContent: 'center' }}>
            <Col style={{ width: '336px' }}>
              <Text bold>Live Context Lookback Minutes</Text>
            </Col>
            <Col flex>
              <OutlinedInput
                name='LiveContextLookbackMinutes'
                type='number'
                margin='dense'
                value={LiveContextLookbackMinutes}
                onChange={handleStreamFormInputChange}
                inputProps={{ min: 1, max: 10 }}
              />
            </Col>
          </Cols>
        </Row>
      )}

      <Row noPadding>
        <Cols style={{ alignItems: 'center' }}>
          <Col>
            <Text title bold>
              Audio Loudness Normalization
            </Text>
          </Col>
          <Col>
            <Switch
              checked={audioNormalizationEnabled}
              onChange={toggleEnableAudioNormalization}
              disabled={!transcoderV3Enabled}
            />
          </Col>
          {betaChipRequired && (
            <Col>
              <BetaTooltipIcon />
            </Col>
          )}
        </Cols>
        <Text muted extraSmall>
          This feature will allow users to configure a target audio normalization level per network.
        </Text>
      </Row>

      <Row
        noVerticalPadding
        extraSmallHorizontalPadding
        style={{
          justifyContent: 'center',
          paddingBottom: spacing.extraSmall,
        }}
      >
        {audioNormalizationEnabled && (
          <Cols
            style={{
              justifyContent: 'left',
              paddingBottom: spacing.extraSmall,
            }}
          >
            <Col
              style={{
                marginLeft: '32px',
                marginRight: '16px',
              }}
            >
              <img src='/assets/shared/svg/Speaker.svg' />
            </Col>
            <Col
              style={{
                width: '256px',
              }}
            >
              <Slider
                value={target}
                min={QUIETEST_AUDIO_NORMALIZATION_LEVEL}
                max={LOUDEST_AUDIO_NORMALIZATION_LEVEL}
                step={1}
                style={{
                  color: `${getColorTheme('loadingBar')}`,
                }}
                marks={AUDIO_NORMALIZATION_SLIDER_MARKINGS}
                onChange={handleSliderChange}
                onChangeCommitted={(_, value) => {
                  // Check is needed because the slider is designed to be either a range of values or just one number. In our case it is always a number.
                  // Values are displayed as negative but stored in networks table as absolute value, so we need to flip the sign when we store.
                  let normalizationLevelToCommit = -DEFAULT_AUDIO_NORMALIZATION_LEVEL;
                  if (!isRangeOfValues(value)) {
                    normalizationLevelToCommit = -value;
                    setAudioNormalizationTarget(normalizationLevelToCommit);
                  }
                }}
              ></Slider>
            </Col>
            <Col horizontalPadding>
              <TextField
                value={targetText}
                style={{
                  width: '48px',
                  paddingBottom: spacing.small,
                  marginLeft: spacing.small,
                }}
                onChange={handleAndCommitTextFieldChange}
              />
            </Col>
            <Col extraSmallVerticalPadding>
              <Text small>LUFS</Text>
            </Col>
            <Col smallHorizontalPadding>
              <Tooltip
                title={generateTooltipForBetaFeatures(LUFS_TOOLTIP_TEXT, LUFS_TOOLTIP_HEADER)}
                placement='right-start'
              >
                <InfoIcon style={{ width: '1rem', color: `${publicaColors.gray}` }} />
              </Tooltip>
            </Col>
          </Cols>
        )}
      </Row>
      <Row noPadding>
        <Cols style={{ alignItems: 'center' }}>
          <Col>
            <Text title bold>
              Enable Padded Ad Breaks
            </Text>
            <Text extraSmall muted>
              Currently supported only for HLS LIVE.
            </Text>
          </Col>
          <Col>
            <Switch
              checked={paddersEnabled}
              onChange={toggleEnablePadders}
              disabled={!transcoderV3Enabled}
            />
          </Col>
          {betaChipRequired && (
            <Col>
              <BetaTooltipIcon />
            </Col>
          )}
        </Cols>
      </Row>
      {/* Only enable for Molotov */}
      {paddersEnabled && publisherID === '802' && (
        <PadderUploaderRow
          title='Regulations'
          description='Carton CSA, plays as preroll.'
          expectedPadderAdType={EPadderType.PADDER_TYPE_BUMPER}
          padderAd={bumperAd}
          PadderAds={PadderAds}
          uploadSSAIPadderAdMediafile={uploadSSAIPadderAdMediafile}
          setSSAIPadderAds={setSSAIPadderAds}
        />
      )}
      {paddersEnabled && (
        <PadderUploaderRow
          title='Bumper'
          description='Short ad that plays at the start and end of ad breaks'
          expectedPadderAdType={EPadderType.PADDER_TYPE_JINGLE}
          padderAd={jingleAd}
          PadderAds={PadderAds}
          uploadSSAIPadderAdMediafile={uploadSSAIPadderAdMediafile}
          setSSAIPadderAds={setSSAIPadderAds}
        />
      )}
    </Rows>
  );
};

interface IPadderUploaderRow {
  title: string;
  description: string;
  expectedPadderAdType: EPadderType;
  padderAd: ISSAIPadderAd | undefined;
  PadderAds: ISSAIPadderAd[];
  uploadSSAIPadderAdMediafile: (
    changeEvent: React.ChangeEvent<HTMLInputElement>,
    padderType: EPadderType,
  ) => void;
  setSSAIPadderAds: (padders: ISSAIPadderAd[]) => void;
}

const PadderUploaderRow = ({
  title,
  description,
  expectedPadderAdType,
  padderAd,
  PadderAds,
  uploadSSAIPadderAdMediafile,
  setSSAIPadderAds,
}: IPadderUploaderRow): ReactElement => {
  return (
    <Row noVerticalPaddingV2>
      <Text title>{title}</Text>
      <Text extraSmall muted>
        {description}
      </Text>
      <Cols>
        <Col>
          <Button component='label'>
            <Text
              color={publicaColors.blue}
              bold
              style={{
                textTransform: 'none',
              }}
            >
              + Add
            </Text>
            <input
              type='file'
              style={{ display: 'none' }}
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                uploadSSAIPadderAdMediafile(evt, expectedPadderAdType);
                evt.target.value = '';
              }}
            />
          </Button>
        </Col>
        {padderAd?.URL && (
          <Col smallVerticalPadding>
            <a
              href={padderAd.URL}
              target='_blank'
              style={{
                color: `${publicaColors.black}`,
              }}
            >
              <Text>{padderAd.URL}</Text>
            </a>
          </Col>
        )}
        {padderAd?.URL && (
          <Col>
            <ButtonV2
              onClick={() => {
                setSSAIPadderAds(
                  PadderAds.filter((padder) => padder.PadderType !== padderAd.PadderType),
                );
              }}
              style={{
                backgroundColor: publicaColors.white,
              }}
            >
              <DeleteIcon
                style={{
                  fill: publicaColors.gray,
                }}
              />
            </ButtonV2>
          </Col>
        )}
      </Cols>
    </Row>
  );
};
